import { useFormContext } from "react-hook-form";
import { Box, CircularProgress } from "@mui/material";
import clsx from "clsx";
import { useEffect } from "react";
import { Button, Modal, Alert } from "$components";
import { useReferencingForm } from "./context/ReferencingFormContext";
import useJStyles from "$hooks/useJStyles";
import { useStyles } from "./styles";
import SubordersSelect from "../SubordersSelect";
import { ReferencesSection } from "./components/ReferencesSection";
import useReception from "$hooks/useReception";
import { Loader } from "$components/Layout/Loader";
import useReferencing from "$hooks/useReferencing";

const ReferencingFormContent = () => {
  const { jclasses } = useJStyles();
  const classes = useStyles();
  const form = useFormContext();
  const { classes: receptionClasses } = useReception();

  const {
    isReadOnly,
    isSavingReception,
    isSavingProducts,
    reception,
    hasReferencingCategory,
    loading,
    isLoading,
    handleOnSubmit,
    handleCancelUpdate,
    openCancelModal,
    setOpenCancelModal,
    openDeleteModal,
    setOpenDeleteModal,
    toDeleteReferences,
    setToDeleteReferences,
    toKeepReferences,
    setToKeepReferences,
    toKeepIdenticalReferences,
    setToKeepIdenticalReferences,
    setToDeleteOrderRef,
    togglePopin,
    setToDeleteProductRefList,
    setConfirmedDeletions,
    onDeleteProducts,
    editedBlocks,
    pendingDeletions,
    initialReceptionReferences,
    productRefListFields,
    orderRefFields,
    onSelectSuborders,
    selectableOrders,
    scrollToRef,
  } = useReferencingForm();
  const { isLoading: isLoadingReferencing } = useReferencing(reception.company);

  const cancelUpdate = () => {
    if (pendingDeletions.length || editedBlocks.length) {
      setOpenCancelModal(true);
    } else {
      togglePopin(false);
    }
  };

  // Scroll to ref if set
  useEffect(() => {
    if (scrollToRef.current) {
      scrollToRef.current.scrollIntoView();
    }
  }, [scrollToRef]);

  // Open delete products modal
  useEffect(() => {
    if (toDeleteReferences.length > 0 && !openDeleteModal) {
      if (!toDeleteReferences.some((item) => item)) {
        onDeleteProducts();
      } else setOpenDeleteModal(true);
    }
  }, [
    openDeleteModal,
    setOpenDeleteModal,
    toDeleteReferences,
    onDeleteProducts,
  ]);

  if (isLoading && isLoadingReferencing) {
    return <Loader text="Chargement des produits..." />;
  }

  return (
    <div className={clsx(jclasses.jWrapper, classes.wrapper)}>
      {/* 
      This section has beeen commented out to have some better perfs on the referencing page ! 
      Due to the fact that ProductSection is read only here and not "that important"
      Should be overrided (someday) to be as fast as possible
       --> Only render some text that summerize this reception ! (No edit needed !)
      */
      /* 
      <ProductsSection
        control={qtyControl}
        register={qtyRegister}
        getValues={qtyGetValues}
        isReadOnly
        setValue={qtySetValue}
        errors={qtyErrors}
        watch={qtyWatch}
        reception={reception}
      /> 
      <div className={jclasses.formSectionHead}>
        <Text type="h3">Référencement</Text>
      </div> */}

      {hasReferencingCategory === false && !loading && (
        <Box mb={2}>
          <Alert severity="error">
            Cette entreprise n&apos;a pas de catégorie “Référencement” dans son
            catalogue.
            <br />
            Si vous souhaitez référencer à la réception, alors veuillez créer
            dans un premier temps la catégorie “Référencement” dans le
            catalogue.
            <br />
            Si vous souhaitez référencer à l&apos;article, alors vous pouvez
            ignorer cette alerte.
          </Alert>
        </Box>
      )}

      <Box className={classes.ordersSelectBlock}>
        <SubordersSelect
          isReadOnly={isReadOnly}
          onSelectedSuborders={onSelectSuborders}
          reception={reception}
          selectableOrders={selectableOrders}
        />
      </Box>

      <form
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault();
          e.stopPropagation();

          form.handleSubmit((data: any) => {
            // Filter here, after validation is complete
            const cleanedData = {
              ...(data as Record<string, any>),
              productReferenceLists: (data?.productReferenceLists ?? [])
                .map((list) => ({
                  ...list,
                  productReferences: list.productReferences.filter(
                    (ref) =>
                      ref.productReference && ref.productReference.trim() !== ""
                  ),
                }))
                .filter((list) => list.productReferences.length > 0),
            };

            handleOnSubmit(cleanedData, false);
          })(e);
        }}
      >
        <ReferencesSection />

        <div className={receptionClasses.footerForm}>
          <Button color="secondary" onClick={() => cancelUpdate()}>
            Annuler
          </Button>
          <Button
            type="submit"
            disabled={
              isSavingReception ||
              isSavingProducts ||
              (initialReceptionReferences.length === 0 &&
                productRefListFields.length === 0 &&
                orderRefFields.length === 0) ||
              (pendingDeletions.length === 0 &&
                editedBlocks.length === 0 &&
                !(
                  initialReceptionReferences.length > 0 &&
                  productRefListFields.length === 0 &&
                  orderRefFields.length === 0
                ))
            }
          >
            {isSavingReception || isSavingProducts ? (
              <CircularProgress
                style={{ width: 18, height: 18, color: "#FFFFFF" }}
              />
            ) : (
              "Enregistrer"
            )}
          </Button>
        </div>
      </form>

      {/* Delete modal */}
      <Modal
        open={openDeleteModal}
        disableExternalClosing
        onCancel={() => {
          setOpenDeleteModal(false);
          setToDeleteReferences([]);
          setToKeepReferences([]);
          setToKeepIdenticalReferences([]);
          setToDeleteOrderRef(null);
          setToDeleteProductRefList(null);
          setConfirmedDeletions(false);
        }}
        title="Suppression de référence"
        textValidate="Supprimer"
        onConfirm={onDeleteProducts}
      >
        <div>
          {toKeepReferences.length > 0 && (
            <>
              Toutes les références de ce référencement à l&apos;exception{" "}
              {toKeepReferences.length > 1
                ? `des références `
                : "de la référence "}
              <b>{toKeepReferences.join(" ,")}</b>
              {" vont être supprimées. "}
            </>
          )}

          {toDeleteReferences.length > 0 &&
            toDeleteReferences.filter(
              (ref) => !toKeepIdenticalReferences.includes(ref)
            ).length > 0 && (
              <>
                La suppression de ce référencement entrainera la suppression{" "}
                {toDeleteReferences.filter(
                  (ref) => !toKeepIdenticalReferences.includes(ref)
                ).length > 1
                  ? "des références suivantes"
                  : "de la référence suivante"}
                {" : "}
                <b>
                  {toDeleteReferences
                    .filter((ref) => !toKeepIdenticalReferences.includes(ref))
                    .join(", ")}
                </b>
              </>
            )}

          {toKeepIdenticalReferences.length > 0 && (
            <div>
              <p>
                Les références suivantes ne seront pas supprimées car elles sont
                identiques aux références originales du catalogue{" "}
                <b>{toKeepIdenticalReferences.join(", ")}</b>
              </p>
            </div>
          )}
        </div>
      </Modal>

      {/* Cancel modal */}
      <Modal
        open={openCancelModal}
        disableExternalClosing
        onCancel={() => setOpenCancelModal(false)}
        title="Confirmation d'abandon des modifications"
        textValidate="Continuer"
        onConfirm={() => handleCancelUpdate()}
      >
        <div>
          Les modifications ou suppressions apportées ne seront pas
          sauvegardées, souhaitez vous continuer ?
        </div>
      </Modal>
    </div>
  );
};

export default ReferencingFormContent;
